import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getAllItems, searchAllItems } from '../services/unifiedSearchService';
import passwordService from '../services/passwordService';
import { addCreditCard, updateCreditCard, deleteCreditCard } from '../services/creditCardService';
import { addApiKey, updateApiKey, deleteApiKey } from '../services/apiKeyService';

const ItemsContext = createContext();

export const useItems = () => {
  const context = useContext(ItemsContext);
  if (!context) {
    throw new Error('useItems must be used within ItemsProvider');
  }
  return context;
};

export const ItemsProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState({
    allItems: [],
    passwords: [],
    creditCards: [],
    apiKeys: [],
    stats: {
      totalItems: 0,
      passwords: 0,
      creditCards: 0,
      apiKeys: 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    types: ['password', 'creditcard', 'apikey'], // 기본값: 모든 타입 표시
    tags: [],
    favoritesOnly: false,
    searchTerm: '',
    category: '',
    sortBy: 'updatedAt'
  });

  // 모든 아이템 로드
  const loadItems = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getAllItems(user.uid);
      setItems(data);
    } catch (error) {
      setError(error.message);
      if (process.env.NODE_ENV === 'development') {
        console.error('아이템 로드 실패:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 아이템 검색
  const searchItems = useCallback(async (searchTerm, searchFilters = filters) => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const results = await searchAllItems(user.uid, searchTerm, searchFilters);
      setItems(prev => ({
        ...prev,
        allItems: results
      }));
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  // 아이템 추가
  const addItem = async (itemType, itemData) => {
    if (!user) return;
    
    setError(null);
    
    try {
      let newItemId;
      
      switch (itemType) {
        case 'password':
          newItemId = await passwordService.addPassword(user.uid, itemData);
          break;
        case 'creditcard':
          newItemId = await addCreditCard(user.uid, itemData);
          break;
        case 'apikey':
          newItemId = await addApiKey(user.uid, itemData);
          break;
        default:
          throw new Error('알 수 없는 아이템 타입');
      }
      
      await loadItems(); // 전체 리로드
      return newItemId;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // 아이템 수정
  const updateItem = async (itemType, itemId, updates) => {
    if (!user) return;
    
    setError(null);
    
    try {
      switch (itemType) {
        case 'password':
          await passwordService.updatePassword(user.uid, itemId, updates);
          break;
        case 'creditcard':
          await updateCreditCard(user.uid, itemId, updates);
          break;
        case 'apikey':
          // 빈 키 값은 업데이트에서 제외
          const apiKeyUpdates = { ...updates };
          if (apiKeyUpdates.keyValue === '') {
            delete apiKeyUpdates.keyValue;
          }
          await updateApiKey(user.uid, itemId, apiKeyUpdates);
          break;
        default:
          throw new Error('알 수 없는 아이템 타입');
      }
      
      await loadItems(); // 전체 리로드
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // 아이템 삭제
  const deleteItem = async (itemType, itemId) => {
    if (!user) return;
    
    setError(null);
    
    try {
      switch (itemType) {
        case 'password':
          await passwordService.deletePassword(user.uid, itemId);
          break;
        case 'creditcard':
          await deleteCreditCard(user.uid, itemId);
          break;
        case 'apikey':
          await deleteApiKey(user.uid, itemId);
          break;
        default:
          throw new Error('알 수 없는 아이템 타입');
      }
      
      await loadItems(); // 전체 리로드
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // 필터 업데이트
  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // 필터 리셋
  const resetFilters = () => {
    setFilters({
      types: ['password', 'creditcard', 'apikey'],
      tags: [],
      favoritesOnly: false,
      searchTerm: '',
      category: '',
      sortBy: 'updatedAt'
    });
  };

  // 사용자 변경 시 아이템 로드
  useEffect(() => {
    if (user) {
      loadItems();
    } else {
      setItems({
        allItems: [],
        passwords: [],
        creditCards: [],
        apiKeys: [],
        stats: {
          totalItems: 0,
          passwords: 0,
          creditCards: 0,
          apiKeys: 0
        }
      });
    }
  }, [user, loadItems]);

  // 필터링된 아이템 계산
  const getFilteredItems = useCallback(() => {
    let filtered = [...items.allItems];
    
    // 검색어 필터링
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const title = item.title || item.cardHolder || item.cardholderName || item.keyName || item.name || '';
        const username = item.username || '';
        const website = item.website || '';
        const provider = item.provider || '';
        const environment = item.environment || '';
        const cardNumber = item.cardNumber || '';
        const cardName = item.cardName || '';
        const bank = item.bank || '';
        const note = item.note || item.notes || '';
        
        // API 키 디버깅용 로그
        if (item.itemType === 'apikey' && process.env.NODE_ENV === 'development') {
          console.log('API Key search:', {
            keyName: item.keyName,
            provider: item.provider,
            searchTerm: searchLower,
            title,
            matches: title.toLowerCase().includes(searchLower) || provider.toLowerCase().includes(searchLower)
          });
        }
        
        return title.toLowerCase().includes(searchLower) ||
               username.toLowerCase().includes(searchLower) ||
               website.toLowerCase().includes(searchLower) ||
               provider.toLowerCase().includes(searchLower) ||
               environment.toLowerCase().includes(searchLower) ||
               (cardNumber && cardNumber.toLowerCase().includes(searchLower)) ||
               cardName.toLowerCase().includes(searchLower) ||
               bank.toLowerCase().includes(searchLower) ||
               note.toLowerCase().includes(searchLower);
      });
    }
    
    // 타입 필터링
    if (filters.types && filters.types.length > 0) {
      filtered = filtered.filter(item => filters.types.includes(item.itemType));
    }
    
    // 즐겨찾기 필터링
    if (filters.favoritesOnly) {
      filtered = filtered.filter(item => item.isFavorite);
    }
    
    // 카테고리 필터링
    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }
    
    // 정렬
    if (filters.sortBy) {
      filtered.sort((a, b) => {
        switch (filters.sortBy) {
          case 'title':
            const titleA = a.title || a.cardHolder || a.cardholderName || a.keyName || a.name || '';
            const titleB = b.title || b.cardHolder || b.cardholderName || b.keyName || b.name || '';
            return titleA.localeCompare(titleB);
          case 'createdAt':
            return (b.createdAt?.toDate?.() || new Date(0)).getTime() - 
                   (a.createdAt?.toDate?.() || new Date(0)).getTime();
          case 'updatedAt':
          default:
            return (b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0)).getTime() - 
                   (a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0)).getTime();
        }
      });
    }
    
    return filtered;
  }, [items.allItems, filters]);

  const value = {
    // 상태
    items,
    loading,
    error,
    filters,
    
    // 액션
    loadItems,
    searchItems,
    addItem,
    updateItem,
    deleteItem,
    updateFilters,
    resetFilters,
    
    // 개별 타입 접근 (하위 호환성)
    passwords: items.passwords,
    creditCards: items.creditCards,
    apiKeys: items.apiKeys,
    allItems: getFilteredItems(), // 필터링된 아이템 제공
    stats: items.stats
  };

  return (
    <ItemsContext.Provider value={value}>
      {children}
    </ItemsContext.Provider>
  );
};

export default ItemsContext;